import { useCallback } from 'react';
import { useAuth } from '../features/auth/contexts/AuthContext';

const BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

export interface ConversationParticipant {
  externalRef: string;
  displayName: string;
  email?: string;
  joinedAt: string;
}

export interface ConversationDTO {
  id: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string;
  participants: ConversationParticipant[];
  messageCount: number;
}

export interface MessageDTO {
  id: string;
  conversationId: string;
  senderExternalRef: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedMessages {
  messages: MessageDTO[];
  hasMore: boolean;
  nextCursor?: string;
  prevCursor?: string;
}

export function useConversationsApi() {
  const { logout } = useAuth();

  const handleUnauthorized = useCallback(async () => {
    try {
      await logout();
    } finally {
      window.location.replace('/login');
    }
  }, [logout]);

  const getConversation = useCallback(
    async (conversationId: string): Promise<ConversationDTO> => {
      const res = await fetch(`${BASE_URL}/conversations-basic/v1/${conversationId}`, {
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to fetch conversation');
      const json = await res.json();
      return json.data as ConversationDTO;
    },
    [handleUnauthorized],
  );

  const getMessages = useCallback(
    async (
      conversationId: string,
      opts?: {
        limit?: number;
        beforeMessageId?: string;
        afterMessageId?: string;
      },
    ): Promise<PaginatedMessages> => {
      const params = new URLSearchParams();
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.beforeMessageId) params.set('beforeMessageId', opts.beforeMessageId);
      if (opts?.afterMessageId) params.set('afterMessageId', opts.afterMessageId);

      const url = `${BASE_URL}/conversations-basic/v1/${conversationId}/messages${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to fetch messages');
      const json = await res.json();
      return json.data as PaginatedMessages;
    },
    [handleUnauthorized],
  );

  return { getConversation, getMessages };
}
